import { BookingClasses } from '../types/booking.type';
import { ClassInstance } from '../types/class.type';
import { TvTypeEnum } from '../types/tvs.type';
import log from '../utils/logger.utils';
import { ApiService } from './api.service';
import { ConfigService } from './config.service';

export class DataFetchService {
    private logger: log.Logger;
    private apiService: ApiService;
    private config: ConfigService;
    private classData?: ClassInstance[];
    private bookingData?: BookingClasses;
    private movementData?: ClassInstance[];

    constructor(apiService: ApiService, config: ConfigService) {
        this.logger = log.getLogger('DataFetchService');
        this.apiService = apiService;
        this.config = config;
    }

    public async startFetching() {
        const tv = this.config.currentTv;
        if (!tv) throw new Error('TV configuration not found');

        this.logger.info(`Starting data fetching for TV type ${tv.type}.`);

        switch (tv.type) {
            case TvTypeEnum.Timer:
            case TvTypeEnum.Exercise:
                await this.refreshClassData();
                setInterval(() => this.refreshClassData(), this.config.tvDataUpdateInterval);
                break;
            case TvTypeEnum.Schedule:
                await this.refreshBookingData();
                setInterval(() => this.refreshBookingData(), this.config.tvDataUpdateInterval);
                break;
            default:
                this.logger.error('Invalid TV type:', tv.type);
                throw new Error('Invalid TV type');
        }
    }

    public async refreshClassData(): Promise<ClassInstance[] | undefined> {
        try {
            const date = this.config.classDate;
            const newClassData = await this.apiService.fetchClassData({ date: date, classInstanceId: this.config.classInstanceId });
            this.classData = newClassData;
            this.logger.trace('Class data has been updated:', this.classData);
            return this.classData;
        } catch (error) {
            this.logger.error('Error fetching class data:', error);
            throw error;
        }
    }

    public async refreshBookingData(): Promise<BookingClasses | undefined> {
        try {
            const date = this.config.classDate;
            const newBookingData = await this.apiService.fetchBookingData(date);
            this.bookingData = newBookingData;
            this.logger.debug('Booking data has been updated:', this.bookingData);
            return this.bookingData;
        } catch (error) {
            this.logger.error('Error fetching booking data:', error);
            throw error;
        }
    }

    public async refreshMovementData(): Promise<ClassInstance[] | undefined> {
        try {
            const date = this.config.classDate;
            const newMovementData = await this.apiService.fetchMovementData(date);
            this.movementData = newMovementData;
            this.logger.debug('Movement data has been updated:', this.movementData);
            return this.movementData;
        } catch (error) {
            this.logger.error('Error fetching movement data:', error);
            throw error;
        }
    }

    public getClassData(): ClassInstance[] | undefined {
        return this.classData;
    }

    public getBookingData(): BookingClasses | undefined {
        return this.bookingData;
    }

    public getMovementData(): ClassInstance[] | undefined {
        return this.movementData;
    }
}